import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import useEditorStore from './useEditorStore';
import { syncQBaseAfterLockingAttachment } from '../lib/socketAlignRedRing';
import { LOFT_CURVATURE_DEFAULT, LOFT_CURVE_POSITION_DEFAULT } from '../services/operations/locking/common/lockingEngulfConstants';
import { PIPELINE_SOCKET_CUT_STRATEGY } from '../services/operations/locking/common/socketCutStrategies';

const useLockingSystemStore = create(
  devtools((set, get) => ({
    // Locking System Tool
    lockingSystem: {
      enabled: true,
      selectedSystem: null,
      showModal: false,
      customizationEnabled: false,
      // Custom parameters (diameter in mm; for bulldog this is the cylinder diameter, typically 68)
      diameter: 68.0,
      depth: 25.0
    },

    // Bull Dog attachment state (UI + positioning only; other attachment types add their own slices)
    bulldog: {
      phase: null,
      adapterMesh: null,
      formMesh: null,
      // Wall angle for the engulfing frustum (degrees). 0 = straight cylinder.
      wallAngleDeg:0,
      // Curvature of the socket↔frustum transition loft, normalized [0, 1].
      // 0 = linear transition; 1 = fullest curve. Default is subtle (near-linear).
      curvature: LOFT_CURVATURE_DEFAULT,
      // Position of the loft's main bend, [0, 1] from top(socket) to bottom(frustum).
      // 0.5 = centred (symmetric); lower moves it toward the socket, higher toward the frustum.
      curvePosition: LOFT_CURVE_POSITION_DEFAULT,
      // Height adjustment added to the adapter's original frustum height
      // (world-space units, typically cm). 0 = use the original GLB height
      // exactly; positive = taller frustum; the pipeline auto-fills any
      // remaining gap to the socket with a straight annular extrusion.
      heightAdjustCm: 0,
      // Which socket-cut strategy the LIVE PREVIEW draws. Defaults to the one the
      // pipeline actually slices with, so the preview matches the build unless the
      // user switches tabs to compare. Switching does NOT change what gets built.
      // See services/operations/locking/common/socketCutStrategies.js.
      socketCutStrategy: PIPELINE_SOCKET_CUT_STRATEGY,
      // True while a lightweight preview (wireframe + projection lines) is
      // displayed in the scene for the user to judge the angle.
      isPreviewing: true,
    },

    fourHoleAttachment: {
      phase: null,
      adapterMesh: null,
      formMesh: null,
      wallAngleDeg: 0,
      // Curvature of the socket↔frustum transition loft, normalized [0, 1].
      // 0 = linear transition; 1 = fullest curve. Default is subtle (near-linear).
      curvature: LOFT_CURVATURE_DEFAULT,
      // Position of the loft's main bend, [0, 1] from top(socket) to bottom(frustum).
      // 0.5 = centred (symmetric); lower moves it toward the socket, higher toward the frustum.
      curvePosition: LOFT_CURVE_POSITION_DEFAULT,
      frustumThicknessCm: 1.0,
      // Which socket-cut strategy the LIVE PREVIEW draws — same meaning as the
      // bulldog slice above (preview-only; the build always uses the pipeline one).
      socketCutStrategy: PIPELINE_SOCKET_CUT_STRATEGY,
      isPreviewing: true,
    },

    /** Set by attachment cards before useToolManager.run(apply); consumed by toolRegistry */
    pendingAttachmentCommand: null,
    setPendingAttachmentCommand: (command) => set({ pendingAttachmentCommand: command }),

    takePendingAttachmentCommand: () => {
      const cmd = get().pendingAttachmentCommand;
      set({ pendingAttachmentCommand: null });
      return cmd;
    },

    /** Latest locking attachment apply command (for in-card Undo + history head sync) */
    currentAttachmentCommand: null,
    setCurrentAttachmentCommand: (command) => set({ currentAttachmentCommand: command }),

    /** Positioning UI: socket align vs attachment move (step 7 attachment cards). */
    positioningPanelTab: 'socket',
    setPositioningPanelTab: (positioningPanelTab) => set({ positioningPanelTab }),

    /** Attachment tab gizmo: translate or rotate the adapter mesh. */
    attachmentTransformMode: 'translate',
    setAttachmentTransformMode: (attachmentTransformMode) => set({ attachmentTransformMode }),

    /**
     * Undo/redo-safe attachment command. Masters are off-scene clones; each execute/undo
     * instantiates a fresh mesh so replaceShadedMesh can dispose the previous instance.
     * @param {object} opts
     * @param {import('three').Mesh} opts.beforeMesh
     * @param {import('three').Mesh} opts.afterMesh
     * @param {string} [opts.attachmentKind='bulldog'] - logical attachment id (see operations/locking)
     */
    createAttachmentCommand: ({ beforeMesh, afterMesh, attachmentKind = 'bulldog' }) => {
      if (!beforeMesh?.isMesh || !afterMesh?.isMesh) {
        console.warn('[Locking] createAttachmentCommand: invalid meshes');
        return null;
      }

      const masterBefore = beforeMesh.clone(true);
      const masterAfter = afterMesh.clone(true);

      const preferredName = beforeMesh.name || afterMesh.name || '';

      const instantiateFromMaster = (masterRoot) => {
        const inst = masterRoot.clone(true);
        if (preferredName) inst.name = preferredName;
        if (inst.isMesh) {
          inst.matrixAutoUpdate = true;
          inst.matrix.decompose(inst.position, inst.quaternion, inst.scale);
        }
        return inst;
      };

      return {
        type: 'LOCKING_ATTACHMENT_APPLY',
        attachmentKind,
        execute: () => {
          const { replaceShadedMesh } = useEditorStore.getState();
          const afterInst = instantiateFromMaster(masterAfter);

          afterInst.userData.lockingWorldBaked = true;
          if (afterInst.userData?.logoPlacementBoundary) {
            useEditorStore.getState().setLogoPlacementBoundary(afterInst.userData.logoPlacementBoundary);
          }

          // CSG output vertices are in world space; object TRS must stay identity.
          afterInst.position.set(0, 0, 0);
          afterInst.quaternion.set(0, 0, 0, 1);
          afterInst.scale.set(1, 1, 1);
          afterInst.updateMatrixWorld(true);

          // CSG bakes a new (world) frame into the vertices while the compare baseline still
          // carries the alignment-step offset — re-frame it so the original stays where it was.
          replaceShadedMesh(afterInst, {
            reframeCompareBaseline: true,
            keepNewMeshTransform: true,
          });

          const editor = useEditorStore.getState();
          // Red-ring deg is baked into CSG output; avoid double-rotating if align tool reopens.
          editor.setAlignmentSocketRedRingXDeg(0);
          editor.setAlignmentSocketAbductionZDeg(0);
          useEditorStore.setState({ alignmentSocketPose: null });
          syncQBaseAfterLockingAttachment(afterInst);
        },
        undo: () => {
          const { replaceShadedMesh } = useEditorStore.getState();
          const restored = instantiateFromMaster(masterBefore);
          delete restored.userData.lockingWorldBaked;
          // keepNewMeshTransform: masterBefore's vertices are in the pre-CSG local frame, so it
          // must keep its own TRS. Without this the identity TRS of the world-baked CSG mesh is
          // copied onto it, which both displaces the socket and leaves the compare baseline
          // reframed into world space while the socket is not — the two stop overlapping.
          replaceShadedMesh(restored, {
            reframeCompareBaseline: true,
            keepNewMeshTransform: true,
          });
        },
      };
    },

    // Actions
    setLockingSystem: (data) => set({ lockingSystem: { ...get().lockingSystem, ...data } }),

    setFourHoleAttachment: (data) => set({ fourHoleAttachment: { ...get().fourHoleAttachment, ...data } }),

    setBulldog: (data) => set({ bulldog: { ...get().bulldog, ...data } }),

    resetBulldog: () => set({
      bulldog: { phase: null, adapterMesh: null, formMesh: null, wallAngleDeg: 0, curvature: LOFT_CURVATURE_DEFAULT, curvePosition: LOFT_CURVE_POSITION_DEFAULT, heightAdjustCm: 0, socketCutStrategy: PIPELINE_SOCKET_CUT_STRATEGY, isPreviewing: false },
      fourHoleAttachment: { phase: null, adapterMesh: null, formMesh: null, wallAngleDeg: 0, curvature: LOFT_CURVATURE_DEFAULT, curvePosition: LOFT_CURVE_POSITION_DEFAULT, frustumThicknessCm: 1.0, socketCutStrategy: PIPELINE_SOCKET_CUT_STRATEGY, isPreviewing: false},
      pendingAttachmentCommand: null,
      currentAttachmentCommand: null,
      positioningPanelTab: 'socket',
      attachmentTransformMode: 'translate',
    }),

    // Reset all data
    reset: () => set({
      lockingSystem: {
        enabled: true,
        selectedSystem: null,
        showModal: false,
        customizationEnabled: false,
        diameter: 68.0,
        depth: 25.0
      },
      bulldog: { phase: null, adapterMesh: null, formMesh: null, wallAngleDeg: 0, curvature: LOFT_CURVATURE_DEFAULT, curvePosition: LOFT_CURVE_POSITION_DEFAULT, heightAdjustCm: 0, socketCutStrategy: PIPELINE_SOCKET_CUT_STRATEGY, isPreviewing: false },
      fourHoleAttachment: { phase: null, adapterMesh: null, formMesh: null, wallAngleDeg: 0, curvature: LOFT_CURVATURE_DEFAULT, curvePosition: LOFT_CURVE_POSITION_DEFAULT, frustumThicknessCm: 1.0, socketCutStrategy: PIPELINE_SOCKET_CUT_STRATEGY, isPreviewing: false},
      pendingAttachmentCommand: null,
      currentAttachmentCommand: null,
      positioningPanelTab: 'socket',
      attachmentTransformMode: 'translate',
    })
  }))
);

/** True while Bull Dog or 4-hole card is in positioning phase. */
export function isLockingAttachmentPositioning() {
  const s = useLockingSystemStore.getState();
  return s.bulldog.phase === 'positioning' || s.fourHoleAttachment.phase === 'positioning';
}

export default useLockingSystemStore;
